import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Users, UserPlus, Crown } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'yeni' | 'daimi'
  const [formData, setFormData] = useState({
    name: "",
    customer_type: "yeni",
    sector: "",
    assigned_user_id: "",
    email: "",
    phone: "",
    notes: "",
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const [cRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCustomers(cRes.data);
      setUsers(uRes.data || []);
    } catch (error) {
      toast.error("Müşteriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        sector: formData.sector || null,
        assigned_user_id: formData.assigned_user_id || null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/customers/${editing.id}`, payload, { withCredentials: true });
        toast.success("Müşteri güncellendi");
      } else {
        await axios.post(`${API_URL}/api/customers`, payload, { withCredentials: true });
        toast.success("Müşteri eklendi");
      }
      fetchCustomers();
      handleClose();
    } catch (error) {
      toast.error("Kaydedilemedi");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu müşteriyi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/customers/${id}`, { withCredentials: true });
      toast.success("Silindi");
      fetchCustomers();
    } catch (error) {
      toast.error("Silinemedi");
    }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setFormData({
      name: c.name,
      customer_type: c.customer_type,
      sector: c.sector || "",
      assigned_user_id: c.assigned_user_id || "",
      email: c.email || "",
      phone: c.phone || "",
      notes: c.notes || "",
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({ name: "", customer_type: "yeni", sector: "", assigned_user_id: "", email: "", phone: "", notes: "" });
  };

  const filtered = customers
    .filter((c) => activeTab === "all" || c.customer_type === activeTab)
    .filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()));

  const yeniCount = customers.filter((c) => c.customer_type === "yeni").length;
  const daimiCount = customers.filter((c) => c.customer_type === "daimi").length;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--text-strong))]"></div></div>;
  }

  const tabs = [
    { id: "all", label: "Tümü", count: customers.length, icon: Users },
    { id: "daimi", label: "Daimi", count: daimiCount, icon: Crown },
    { id: "yeni", label: "Yeni", count: yeniCount, icon: UserPlus },
  ];

  return (
    <div className="p-8" data-testid="customers-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[hsl(var(--text-strong))] mb-2">Müşteriler</h1>
          <p className="text-sm text-[hsl(var(--text-soft))] font-light">Toplam {customers.length} müşteri · {daimiCount} daimi · {yeniCount} yeni</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-customer-button"
          className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-medium text-sm shadow-premium hover:opacity-90 premium-hover"
        >
          <Plus size={18} weight="bold" />
          Yeni Müşteri
        </button>
      </div>

      {/* Tabs + Search */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-1 p-1 rounded-xl bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]" data-testid="customer-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === t.id
                  ? "bg-[hsl(var(--surface-elevated))] text-[hsl(var(--text-strong))] shadow-premium-sm"
                  : "text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text-strong))]"
              }`}
            >
              <t.icon size={14} weight="duotone" />
              {t.label}
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[hsl(var(--background))] text-[hsl(var(--text-soft))]">{t.count}</span>
            </button>
          ))}
        </div>

        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-faint))]" weight="bold" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Müşteri ara..."
            data-testid="customer-search-input"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] text-sm text-[hsl(var(--text-strong))] focus:border-[hsl(var(--text-strong))] focus:outline-none"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] overflow-hidden shadow-premium-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Müşteri</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Tip</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Sektör</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Marka Danışmanı</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">E-posta</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">Telefon</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[hsl(var(--text-soft))]">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-[hsl(var(--text-faint))]">Kayıt bulunamadı</td></tr>
              ) : filtered.map((c, idx) => (
                <tr key={c.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]/40" data-testid={`customer-row-${idx}`}>
                  <td className="px-5 py-3.5 text-sm font-medium text-[hsl(var(--text-strong))]">{c.name}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-block px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                      c.customer_type === "daimi"
                        ? "bg-[hsl(var(--text-strong))] text-[hsl(var(--background))]"
                        : "bg-[hsl(var(--secondary))] text-[hsl(var(--text-strong))] border border-[hsl(var(--border))]"
                    }`}>
                      {c.customer_type}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    {c.sector ? (
                      <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/20">
                        {c.sector === "e-ticaret" ? "E-Ticaret" : c.sector === "hizmet" ? "Hizmet" : c.sector}
                      </span>
                    ) : <span className="text-xs text-[hsl(var(--text-faint))]">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-soft))]">
                    {c.assigned_user_name || <span className="text-xs text-[hsl(var(--text-faint))]">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-soft))]">{c.email || "-"}</td>
                  <td className="px-5 py-3.5 text-sm text-[hsl(var(--text-soft))] tabular-nums">{c.phone || "-"}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => handleEdit(c)} data-testid={`edit-customer-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--secondary))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text-strong))]">
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      <button onClick={() => handleDelete(c.id)} data-testid={`delete-customer-${idx}`} className="p-2 rounded-lg hover:bg-[hsl(var(--danger))]/10 text-[hsl(var(--text-soft))] hover:text-[hsl(var(--danger))]">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="rounded-2xl border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] max-w-md" data-testid="customer-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold tracking-tight text-[hsl(var(--text-strong))]">{editing ? "Müşteriyi Düzenle" : "Yeni Müşteri"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-form">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Ad *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="customer-name-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Tip *</label>
              <select value={formData.customer_type} onChange={(e) => setFormData({ ...formData, customer_type: e.target.value })} data-testid="customer-type-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="yeni">Yeni Müşteri</option>
                <option value="daimi">Daimi Müşteri</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Sektör</label>
              <select value={formData.sector} onChange={(e) => setFormData({ ...formData, sector: e.target.value })} data-testid="customer-sector-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="">Seçiniz (opsiyonel)</option>
                <option value="hizmet">Hizmet Sektörü</option>
                <option value="e-ticaret">E-Ticaret</option>
                <option value="diger">Diğer</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Marka Danışmanı</label>
              <select value={formData.assigned_user_id} onChange={(e) => setFormData({ ...formData, assigned_user_id: e.target.value })} data-testid="customer-assigned-user-select" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]">
                <option value="">Atanmamış</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role === "admin" ? "Admin" : u.role === "yonetici" ? "Yönetici" : "Personel"})</option>
                ))}
              </select>
              {formData.assigned_user_id && users.find((u) => u.id === formData.assigned_user_id)?.role === "personel" && (
                <p className="text-[10px] text-[hsl(var(--text-faint))] mt-1.5">
                  Bu müşteri, atanan personelin görebileceği listeye otomatik eklenir.
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">E-posta</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="customer-email-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Telefon</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="customer-phone-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--text-soft))] mb-2">Notlar</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} data-testid="customer-notes-input" className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm text-[hsl(var(--text-strong))]" />
            </div>
            <div className="flex gap-2 pt-3">
              <button type="button" onClick={handleClose} data-testid="cancel-customer-button" className="flex-1 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--secondary))]">İptal</button>
              <button type="submit" data-testid="save-customer-button" className="flex-1 px-4 py-2.5 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 shadow-premium-sm">Kaydet</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
